import React, { useState, useEffect, useCallback } from 'react';
import {
    X,
    User,
    Mail,
    Phone,
    Calendar,
    Briefcase,
    FileText,
    Save,
    AlertCircle,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    Shield,
    Rocket,
    Users,
    Clock,
    GraduationCap,
    UserCheck,
    UserMinus,
    XCircle,
    Check,
    Building2,
    Heart,
    Info,
    Plus,
    Settings,
    ArrowRight,
} from 'lucide-react';
import UserSearchDropdown from './UserSearchDropdown';
import CreateRoleModal from './CreateRoleModal';
import api from '../utils/api';

const EditEmployeeModal = ({ isOpen, onClose, onSuccess, employee }) => {
    const [loading, setLoading] = useState(false);
    const [showEmailConfirm, setShowEmailConfirm] = useState(false);
    const [showMoveToOnboardingConfirm, setShowMoveToOnboardingConfirm] = useState(false);
    const [originalEmail, setOriginalEmail] = useState('');

    // Data lists
    const [availableRoles, setAvailableRoles] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    const [permissionCategories, setPermissionCategories] = useState([]);

    // Accordion state
    const [expandedSections, setExpandedSections] = useState({
        basicInfo: true,
        rolePermissions: false,
        orgStructure: false,
        supportTeam: false,
        statusDates: false,
        additionalDetails: false,
    });

    // Inline role creation
    const [showInlineRoleCreation, setShowInlineRoleCreation] = useState(false);
    const [inlineRoleName, setInlineRoleName] = useState('');
    const [inlineRoleDescription, setInlineRoleDescription] = useState('');
    const [inlineRolePermissions, setInlineRolePermissions] = useState({});
    const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
    const [inlineRoleData, setInlineRoleData] = useState(null);
    const [showPermissionsPreview, setShowPermissionsPreview] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        start_date: '',
        additional_details: '',
        lifecycle_stage: '',
        preboarding_start_date: '',
        employee_actual_start_date: '',
        team_ids: [],
        role_id: null,
        // Support team (user objects for UserSearchDropdown)
        manager: null,
        team_lead: null,
        buddy: null,
        hr_contact: null,
    });

    // Validation state
    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({});

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-\(\)\+]{7,}$/;

    const validateField = (name, value) => {
        switch (name) {
            case 'email':
                if (!value) return 'Email is required';
                if (!emailRegex.test(value)) return 'Please enter a valid email address';
                return '';
            case 'phone':
                if (value && !phoneRegex.test(value)) return 'Please enter a valid phone number';
                return '';
            case 'first_name':
                if (!value || !value.trim()) return 'First name is required';
                return '';
            case 'last_name':
                if (!value || !value.trim()) return 'Last name is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const errors = {};
        errors.first_name = validateField('first_name', formData.first_name);
        errors.last_name = validateField('last_name', formData.last_name);
        errors.email = validateField('email', formData.email);
        errors.phone = validateField('phone', formData.phone);
        return errors;
    };

    const isFormValid = () => {
        const errors = validateForm();
        return !errors.first_name && !errors.last_name && !errors.email && !errors.phone;
    };

    // Fetch data when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchRoles();
            fetchTeams();
            fetchPermissionCategories();
        }
    }, [isOpen]);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await api.get('/teams');
            setAvailableTeams(response.data.teams || []);
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const fetchPermissionCategories = async () => {
        try {
            const response = await api.get('/employees/roles/templates');
            setPermissionCategories(response.data.categories || []);
        } catch (error) {
            console.error('Error fetching permission categories:', error);
        }
    };

    const fetchEmployeeTeams = async (employeeId) => {
        try {
            const response = await api.get(`/teams/employee/${employeeId}`);
            return response.data.teams?.map(t => t.id) || [];
        } catch (error) {
            console.error('Error fetching employee teams:', error);
            return [];
        }
    };

    // Resolve a user ID to a user object for UserSearchDropdown
    const resolveUserById = async (userId) => {
        if (!userId) return null;
        try {
            const response = await api.get(`/employees/${userId}`);
            const emp = response.data.employee;
            if (emp) {
                return { id: emp.id, first_name: emp.first_name, last_name: emp.last_name, email: emp.email, position: emp.position };
            }
        } catch (error) {
            console.error(`Error resolving user ${userId}:`, error);
        }
        return null;
    };

    // Load employee data when modal opens
    useEffect(() => {
        const loadEmployeeData = async () => {
            if (isOpen && employee) {
                let teamIds = [];
                try {
                    teamIds = await fetchEmployeeTeams(employee.id);
                } catch (e) {
                    console.error('Failed to fetch teams:', e);
                }

                // Resolve support team user IDs to objects
                const [managerObj, teamLeadObj, buddyObj, hrContactObj] = await Promise.all([
                    resolveUserById(employee.manager_id),
                    resolveUserById(employee.team_lead_id),
                    resolveUserById(employee.buddy_id),
                    resolveUserById(employee.hr_contact_id),
                ]);

                setFormData({
                    first_name: employee.first_name || '',
                    last_name: employee.last_name || '',
                    email: employee.email || '',
                    phone: employee.phone || '',
                    position: employee.position || '',
                    start_date: employee.start_date ? employee.start_date.split('T')[0] : '',
                    additional_details: employee.additional_details || '',
                    lifecycle_stage: employee.lifecycle_stage || 'pending',
                    preboarding_start_date: employee.preboarding_start_date ? employee.preboarding_start_date.split('T')[0] : '',
                    employee_actual_start_date: employee.employee_actual_start_date ? employee.employee_actual_start_date.split('T')[0] : '',
                    team_ids: teamIds,
                    role_id: employee.role_id || null,
                    manager: managerObj,
                    team_lead: teamLeadObj,
                    buddy: buddyObj,
                    hr_contact: hrContactObj,
                });

                setOriginalEmail(employee.email || '');
                setValidationErrors({});
                setTouchedFields({});
                setShowEmailConfirm(false);
                setShowMoveToOnboardingConfirm(false);
                setShowInlineRoleCreation(false);
                setShowPermissionsPreview(false);
                setExpandedSections({
                    basicInfo: true,
                    rolePermissions: false,
                    orgStructure: false,
                    supportTeam: false,
                    statusDates: false,
                    additionalDetails: false,
                });
            }
        };
        loadEmployeeData();
    }, [isOpen, employee]);

    const isEmailChanged = formData.email !== originalEmail;
    const isPreboardingEmployee = employee?.lifecycle_stage === 'preboarding';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData((prev) => ({ ...prev, [name]: newValue }));
        if (touchedFields[name]) {
            const error = validateField(name, newValue);
            setValidationErrors((prev) => ({ ...prev, [name]: error }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouchedFields((prev) => ({ ...prev, [name]: true }));
        const error = validateField(name, value);
        setValidationErrors((prev) => ({ ...prev, [name]: error }));
    };

    const handleRoleChange = (e) => {
        const value = e.target.value;
        if (value === 'create_new') {
            setShowInlineRoleCreation(true);
            setInlineRoleName('');
            setInlineRoleDescription('');
            setInlineRolePermissions({});
        } else {
            setShowInlineRoleCreation(false);
            const selectedRole = availableRoles.find(r => typeof r === 'object' && r.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                role_id: selectedRole ? selectedRole.id : null,
                position: selectedRole ? selectedRole.role_name : value,
            }));
        }
    };

    const handleTeamToggle = (teamId) => {
        setFormData((prev) => ({
            ...prev,
            team_ids: prev.team_ids.includes(teamId)
                ? prev.team_ids.filter((id) => id !== teamId)
                : [...prev.team_ids, teamId],
        }));
    };

    // Inline role creation
    const handleInlinePermissionToggle = (categoryKey) => {
        setInlineRolePermissions(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
    };

    const handleCreateRoleInline = async () => {
        if (!inlineRoleName.trim()) return;
        setLoading(true);
        try {
            const permissions = {};
            permissionCategories.forEach(category => {
                if (inlineRolePermissions[category.key]) {
                    if (category.permissions) {
                        category.permissions.forEach(perm => { permissions[perm.key] = true; });
                    }
                }
            });
            const response = await api.post('/employees/roles', {
                role_name: inlineRoleName.trim(),
                description: inlineRoleDescription.trim(),
                permissions,
            });
            if (response.data.role) {
                await fetchRoles();
                setFormData(prev => ({
                    ...prev,
                    role_id: response.data.role.id,
                    position: response.data.role.role_name,
                }));
                setShowInlineRoleCreation(false);
            }
        } catch (error) {
            console.error('Error creating role:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomisePermissions = () => {
        setInlineRoleData({
            role_name: inlineRoleName,
            description: inlineRoleDescription,
            permissions: inlineRolePermissions,
        });
        setShowCreateRoleModal(true);
    };

    const handleFullRoleCreated = async () => {
        await fetchRoles();
        setShowCreateRoleModal(false);
        setShowInlineRoleCreation(false);
        setInlineRoleData(null);
        try {
            const response = await api.get('/employees/roles');
            const roles = response.data.roles || [];
            const newest = roles.reduce((latest, role) => {
                if (!latest || new Date(role.created_at) > new Date(latest.created_at)) return role;
                return latest;
            }, null);
            if (newest) {
                setFormData(prev => ({ ...prev, role_id: newest.id, position: newest.role_name }));
            }
        } catch (err) {
            console.error('Error fetching newest role:', err);
        }
    };

    const handleMoveToOnboarding = () => {
        setShowMoveToOnboardingConfirm(true);
    };

    const confirmMoveToOnboarding = async () => {
        setLoading(true);
        try {
            const response = await api.put(`/employees/${employee.id}`, { move_to_onboarding: true });
            onSuccess(response.data.employee, false);
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to move employee to onboarding';
            setValidationErrors((prev) => ({ ...prev, submit: errorMessage }));
        } finally {
            setLoading(false);
            setShowMoveToOnboardingConfirm(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        setValidationErrors(errors);
        setTouchedFields({ first_name: true, last_name: true, email: true, phone: true });
        if (errors.first_name || errors.last_name || errors.email || errors.phone) return;
        if (isEmailChanged && !showEmailConfirm) {
            setShowEmailConfirm(true);
            return;
        }
        await saveChanges();
    };

    const saveChanges = async () => {
        setLoading(true);
        try {
            const updateData = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone: formData.phone,
                position: formData.position,
                start_date: formData.start_date,
                additional_details: formData.additional_details,
                lifecycle_stage: formData.lifecycle_stage,
                preboarding_start_date: formData.preboarding_start_date || null,
                employee_actual_start_date: formData.employee_actual_start_date || null,
                team_ids: formData.team_ids,
                role_id: formData.role_id,
                manager_id: formData.manager?.id || null,
                team_lead_id: formData.team_lead?.id || null,
                buddy_id: formData.buddy?.id || null,
                hr_contact_id: formData.hr_contact?.id || null,
            };
            const response = await api.put(`/employees/${employee.id}`, updateData);
            onSuccess(response.data.employee, response.data.emailChanged);
            onClose();
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to update employee';
            setValidationErrors((prev) => ({ ...prev, submit: errorMessage }));
            setShowEmailConfirm(false);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !employee) return null;

    const inputClassName = (fieldName) =>
        `w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all ${validationErrors[fieldName] && touchedFields[fieldName]
            ? 'border-red-500 focus:ring-red-200'
            : 'border-border focus:ring-primary'
        }`;

    const renderError = (fieldName) => {
        if (validationErrors[fieldName] && touchedFields[fieldName]) {
            return (
                <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {validationErrors[fieldName]}
                </p>
            );
        }
        return null;
    };

    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    const getExcludeIds = (excludeField) => {
        const ids = [];
        ['manager', 'team_lead', 'buddy', 'hr_contact'].forEach(field => {
            if (field !== excludeField && formData[field]?.id) {
                ids.push(formData[field].id);
            }
        });
        return ids;
    };

    // Get selected role's permissions for preview
    const getSelectedRolePermissions = () => {
        if (!formData.role_id) return null;
        const selectedRole = availableRoles.find(r => typeof r === 'object' && r.id === formData.role_id);
        return selectedRole?.permissions || null;
    };

    // Permission display labels
    const permissionDisplayLabels = {
        'employee.add': 'Add employees', 'employee.edit': 'Edit employees', 'employee.delete': 'Delete employees',
        'employee.view_details': 'View employee details', 'employee.change_roles': 'Change employee roles',
        'employee.lifecycle': 'Manage lifecycle', 'employee.view_history': 'View employee history',
        'employee.offboard': 'Offboard employees', 'employee.reactivate': 'Reactivate employees',
        'task.create': 'Create tasks', 'task.view': 'View tasks', 'task.edit': 'Edit tasks', 'task.delete': 'Delete tasks',
        'task.assign': 'Assign tasks', 'task.review': 'Review submissions', 'task.template_create': 'Create templates',
        'task.template_edit': 'Edit templates', 'task.template_use': 'Use templates', 'task.template_schedule': 'Schedule templates',
        'task.draft_manage': 'Manage drafts', 'task.bulk_actions': 'Bulk actions',
        'file.upload': 'Upload files', 'file.view': 'View files', 'file.edit': 'Edit files', 'file.delete': 'Delete files',
        'file.category_manage': 'Manage categories', 'file.view_history': 'View file history', 'file.folder_manage': 'Manage folders',
        'file.integration_import': 'Import from integrations', 'file.bulk_download': 'Bulk download',
        'notice.create': 'Create notices', 'notice.view': 'View notices', 'notice.edit': 'Edit notices',
        'notice.delete': 'Delete notices', 'notice.view_analytics': 'View notice analytics', 'notice.messaging': 'Notice messaging',
        'course.create': 'Create courses', 'course.view': 'View courses', 'course.edit': 'Edit courses', 'course.delete': 'Delete courses',
        'course.enrollment': 'Manage enrollment', 'course.templates': 'Manage templates', 'course.view_analytics': 'View course analytics',
        'course.draft_manage': 'Manage drafts',
        'compliance.full_manage': 'Full compliance management', 'compliance.create_tasks': 'Create compliance tasks',
        'compliance.view_reports': 'View compliance reports', 'compliance.export_reports': 'Export compliance reports',
        'compliance.resolve_violations': 'Resolve violations',
        'analytics.company_wide': 'Company-wide analytics', 'analytics.employee_progress': 'Employee progress',
        'analytics.task_analytics': 'Task analytics', 'analytics.file_analytics': 'File analytics',
        'analytics.ai_metrics': 'AI assistant metrics', 'analytics.login_stats': 'Login statistics',
        'analytics.productivity': 'Productivity metrics', 'analytics.export': 'Export analytics',
        'feedback.view': 'View feedback', 'feedback.respond': 'Respond to feedback',
        'survey.create': 'Create surveys', 'survey.edit': 'Edit surveys', 'survey.view_responses': 'View survey responses',
        'survey.export_responses': 'Export survey responses', 'survey.delete': 'Delete surveys', 'survey.manage_recurring': 'Manage recurring surveys',
        'role.create': 'Create roles', 'role.edit': 'Edit roles', 'role.delete': 'Delete roles', 'role.assign': 'Assign roles',
        'team.create': 'Create teams', 'team.edit': 'Edit teams', 'team.delete': 'Delete teams', 'team.manage_members': 'Manage team members',
        'system.settings': 'Manage settings', 'system.integrations': 'Manage integrations', 'system.permission_config': 'Configure permissions',
        'system.audit_view': 'View audit logs', 'system.audit_export': 'Export audit logs', 'system.notifications': 'Manage notifications',
        'system.email_templates': 'Manage email templates', 'system.health': 'View system health', 'system.billing': 'Manage billing',
        'system.backup': 'Manage backups', 'system.api_keys': 'Manage API keys',
        'ai.use_assistant': 'Use AI assistant', 'ai.start_conversations': 'Start AI conversations',
        'ai.upload_files': 'Upload files to AI', 'ai.task_analysis': 'AI task analysis', 'ai.file_analysis': 'AI file analysis',
        'lifecycle.create_preboarding': 'Create preboarding content', 'lifecycle.view_preboarding': 'View preboarding content',
        'lifecycle.create_onboarding': 'Create onboarding content', 'lifecycle.view_onboarding': 'View onboarding content',
    };

    const groupPermissionsByCategory = (permissions) => {
        const categories = {
            'Employee Management': [], 'Task Management': [], 'File Management': [], 'Notices': [], 'Courses': [],
            'Compliance': [], 'Analytics': [], 'Feedback & Surveys': [], 'Roles & Teams': [], 'System Admin': [],
            'AI Assistant': [], 'Lifecycle': [],
        };
        Object.entries(permissions).forEach(([key, value]) => {
            if (!value || value === 'none') return;
            const label = permissionDisplayLabels[key] || key;
            const scope = typeof value === 'string' && value !== 'true' ? value : null;
            const displayValue = scope ? `${label} (${scope.replace(/_/g, ' ')})` : label;
            if (key.startsWith('employee.')) categories['Employee Management'].push({ key, label: displayValue });
            else if (key.startsWith('task.')) categories['Task Management'].push({ key, label: displayValue });
            else if (key.startsWith('file.')) categories['File Management'].push({ key, label: displayValue });
            else if (key.startsWith('notice.')) categories['Notices'].push({ key, label: displayValue });
            else if (key.startsWith('course.')) categories['Courses'].push({ key, label: displayValue });
            else if (key.startsWith('compliance.')) categories['Compliance'].push({ key, label: displayValue });
            else if (key.startsWith('analytics.')) categories['Analytics'].push({ key, label: displayValue });
            else if (key.startsWith('feedback.') || key.startsWith('survey.')) categories['Feedback & Surveys'].push({ key, label: displayValue });
            else if (key.startsWith('role.') || key.startsWith('team.')) categories['Roles & Teams'].push({ key, label: displayValue });
            else if (key.startsWith('system.')) categories['System Admin'].push({ key, label: displayValue });
            else if (key.startsWith('ai.')) categories['AI Assistant'].push({ key, label: displayValue });
            else if (key.startsWith('lifecycle.')) categories['Lifecycle'].push({ key, label: displayValue });
        });
        return Object.fromEntries(Object.entries(categories).filter(([_, perms]) => perms.length > 0));
    };

    const permissionCategoryLabels = {
        employee: 'Employee Management', task: 'Task Management', file: 'File Management',
        notice: 'Notices & Communication', analytics: 'Analytics & Reporting', system: 'System Administration',
    };

    const getStatusIcon = (lifecycle_stage) => {
        switch (lifecycle_stage) {
            case 'pending': return <Clock size={16} className="text-amber-500" />;
            case 'preboarding': return <Rocket size={16} className="text-purple-500" />;
            case 'onboarding': return <GraduationCap size={16} className="text-blue-500" />;
            case 'active': return <UserCheck size={16} className="text-emerald-500" />;
            case 'offboarding': return <UserMinus size={16} className="text-orange-500" />;
            case 'departed': return <XCircle size={16} className="text-red-500" />;
            default: return <User size={16} className="text-gray-400" />;
        }
    };

    const getStatusBadge = () => {
        const status = employee.display_status || employee.lifecycle_stage;
        const statusColors = {
            preboarding: 'bg-purple-100 text-purple-700 border-purple-200',
            onboarding: 'bg-blue-100 text-blue-700 border-blue-200',
            active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            pending: 'bg-amber-100 text-amber-700 border-amber-200',
        };
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusColors[status] || 'bg-muted text-muted-foreground border-transparent'}`}>
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    // Section header component (matching Add modal style)
    const SectionHeader = ({ icon: Icon, title, subtitle, sectionKey, stepNumber, isRequired }) => (
        <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors rounded-xl"
        >
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-bold">
                    {stepNumber}
                </div>
                <div className="text-left">
                    <h4 className="font-semibold text-foreground text-sm flex items-center gap-2">
                        {title}
                        {isRequired && <span className="text-red-500 text-xs">*</span>}
                    </h4>
                    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {expandedSections[sectionKey] ? (
                <ChevronUp size={18} className="text-muted-foreground" />
            ) : (
                <ChevronDown size={18} className="text-muted-foreground" />
            )}
        </button>
    );

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4 transition-all">
                <div className="bg-card rounded-none md:rounded-2xl shadow-2xl w-full max-w-3xl h-full md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 flex items-center justify-between p-4 md:p-6 border-b border-border safe-area-top">
                        <div className="flex items-center gap-4">
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-foreground">Edit Employee</h2>
                                <p className="text-xs md:text-sm text-muted-foreground mt-1">Update employee information</p>
                            </div>
                            {getStatusBadge()}
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
                            <X size={24} className="text-muted-foreground" />
                        </button>
                    </div>

                    {/* Move to Onboarding Action (for preboarding employees) */}
                    {isPreboardingEmployee && !showMoveToOnboardingConfirm && (
                        <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Rocket size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-foreground">Move to Onboarding Early</p>
                                        <p className="text-sm text-muted-foreground">Transition employee to onboarding immediately</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleMoveToOnboarding}
                                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                                >
                                    Start Onboarding
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Move to Onboarding Confirmation */}
                    {showMoveToOnboardingConfirm && (
                        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <div className="flex items-start gap-3">
                                <AlertTriangle size={24} className="text-amber-600 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="font-semibold text-amber-800">Confirm Move to Onboarding</p>
                                    <p className="text-sm text-amber-700 mt-1">
                                        This will immediately transition <strong>{employee.first_name} {employee.last_name}</strong> from preboarding to onboarding.
                                    </p>
                                    <div className="flex gap-3 mt-4">
                                        <button
                                            onClick={() => setShowMoveToOnboardingConfirm(false)}
                                            className="px-4 py-2 text-foreground hover:bg-muted rounded-lg font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmMoveToOnboarding}
                                            disabled={loading}
                                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {loading ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            ) : (
                                                <Rocket size={16} />
                                            )}
                                            Confirm Move
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex-1 p-4 md:p-6 overflow-y-auto">
                        {validationErrors.submit && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                                <AlertCircle size={16} />
                                {validationErrors.submit}
                            </div>
                        )}

                        <div className="space-y-3">

                            {/* ─── SECTION 1: Basic Info ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={User}
                                    title="Basic Information"
                                    subtitle="Name, email, and contact details"
                                    sectionKey="basicInfo"
                                    stepNumber={1}
                                    isRequired
                                />
                                {expandedSections.basicInfo && (
                                    <div className="px-4 pb-4 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2">
                                                    First Name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                    <input
                                                        type="text"
                                                        name="first_name"
                                                        value={formData.first_name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`${inputClassName('first_name')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                        placeholder="John"
                                                    />
                                                </div>
                                                {renderError('first_name')}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2">
                                                    Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                    <input
                                                        type="text"
                                                        name="last_name"
                                                        value={formData.last_name}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className={`${inputClassName('last_name')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                        placeholder="Doe"
                                                    />
                                                </div>
                                                {renderError('last_name')}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2">
                                                Email <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className={`${inputClassName('email')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                    placeholder="john.doe@company.com"
                                                />
                                            </div>
                                            {renderError('email')}
                                            {isEmailChanged && !validationErrors.email && (
                                                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                                    <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div className="text-sm">
                                                        <p className="font-medium text-amber-800">Email change requires password reset</p>
                                                        <ul className="list-disc list-inside text-amber-700 mt-1 space-y-0.5">
                                                            <li>Send a notification to the old email</li>
                                                            <li>Send a password reset link to the new email</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2">Phone Number</label>
                                            <div className="relative">
                                                <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className={`${inputClassName('phone')} bg-background text-foreground placeholder:text-muted-foreground`}
                                                    placeholder="+1 (555) 000-0000"
                                                />
                                            </div>
                                            {renderError('phone')}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 2: Role & Permissions ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Shield}
                                    title="Role & Permissions"
                                    subtitle="Role assignment and permissions"
                                    sectionKey="rolePermissions"
                                    stepNumber={2}
                                />
                                {expandedSections.rolePermissions && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Role Dropdown */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2">Role</label>
                                            <div className="relative">
                                                <Briefcase size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                <select
                                                    name="role"
                                                    value={showInlineRoleCreation ? 'create_new' : (formData.role_id?.toString() || '')}
                                                    onChange={handleRoleChange}
                                                    className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none bg-background text-foreground"
                                                >
                                                    <option value="">Select a role</option>
                                                    {availableRoles.map((role) => (
                                                        <option
                                                            key={typeof role === 'string' ? role : role.id}
                                                            value={typeof role === 'string' ? role : role.id}
                                                        >
                                                            {typeof role === 'string' ? role : role.role_name}
                                                        </option>
                                                    ))}
                                                    <option value="create_new">+ Create New Role</option>
                                                </select>
                                                <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Role Permissions Preview */}
                                        {formData.role_id && !showInlineRoleCreation && getSelectedRolePermissions() && (
                                            <div className="animate-in slide-in-from-top-2 duration-200 border border-border rounded-xl overflow-visible">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPermissionsPreview(!showPermissionsPreview)}
                                                    className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Shield size={18} className="text-primary" />
                                                        <div className="text-left">
                                                            <h4 className="font-semibold text-foreground text-sm">Role Permissions</h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                {Object.keys(getSelectedRolePermissions() || {}).filter(k => {
                                                                    const v = getSelectedRolePermissions()[k];
                                                                    return v && v !== 'none';
                                                                }).length} permissions enabled for "{formData.position || 'selected role'}"
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {showPermissionsPreview ? (
                                                        <ChevronUp size={18} className="text-muted-foreground" />
                                                    ) : (
                                                        <ChevronDown size={18} className="text-muted-foreground" />
                                                    )}
                                                </button>
                                                {showPermissionsPreview && (
                                                    <div className="p-4 bg-card max-h-80 overflow-y-auto">
                                                        {(() => {
                                                            const permissions = getSelectedRolePermissions();
                                                            const grouped = groupPermissionsByCategory(permissions);
                                                            if (Object.keys(grouped).length === 0) {
                                                                return <p className="text-sm text-muted-foreground text-center py-4">No permissions configured</p>;
                                                            }
                                                            return (
                                                                <div className="space-y-4">
                                                                    {Object.entries(grouped).map(([category, perms]) => (
                                                                        <div key={category}>
                                                                            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{category}</h5>
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {perms.map((perm) => (
                                                                                    <span key={perm.key} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-md border border-emerald-200">
                                                                                        <Check size={10} className="text-emerald-600" />
                                                                                        {perm.label}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Inline Role Creation Panel */}
                                        {showInlineRoleCreation && (
                                            <div className="animate-in slide-in-from-top-2 duration-200 bg-muted/30 rounded-xl p-4 border border-border">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <Plus size={18} className="text-primary" />
                                                    <h4 className="font-semibold text-foreground">Create New Role</h4>
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                                        Role Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={inlineRoleName}
                                                        onChange={(e) => setInlineRoleName(e.target.value)}
                                                        className="w-full px-4 py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground placeholder:text-muted-foreground"
                                                        placeholder="e.g., Marketing Manager"
                                                    />
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-1.5">Description (Optional)</label>
                                                    <input
                                                        type="text"
                                                        value={inlineRoleDescription}
                                                        onChange={(e) => setInlineRoleDescription(e.target.value)}
                                                        className="w-full px-4 py-2.5 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground placeholder:text-muted-foreground"
                                                        placeholder="Brief description of this role"
                                                    />
                                                </div>
                                                <div className="mb-4">
                                                    <label className="block text-sm font-medium text-foreground mb-2">Permissions</label>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                        {permissionCategories.slice(0, 6).map((category) => (
                                                            <button
                                                                key={category.key}
                                                                type="button"
                                                                onClick={() => handleInlinePermissionToggle(category.key)}
                                                                className={`p-3 rounded-lg border transition-all flex items-center gap-2 text-left ${inlineRolePermissions[category.key]
                                                                    ? 'border-primary bg-primary/10 text-primary'
                                                                    : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                                                                    }`}
                                                            >
                                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${inlineRolePermissions[category.key]
                                                                    ? 'bg-primary text-white'
                                                                    : 'bg-muted'
                                                                    }`}>
                                                                    {inlineRolePermissions[category.key] && <Check size={12} />}
                                                                </div>
                                                                <span className="text-xs font-medium truncate">
                                                                    {permissionCategoryLabels[category.key] || category.label}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between pt-3 border-t border-border">
                                                    <button type="button" onClick={handleCustomisePermissions} className="text-sm text-primary hover:text-primary-dark font-medium flex items-center gap-1">
                                                        <Settings size={14} />
                                                        Customise permissions
                                                    </button>
                                                    <div className="flex gap-2">
                                                        <button type="button" onClick={() => setShowInlineRoleCreation(false)} className="px-4 py-2 text-sm text-muted-foreground hover:bg-muted rounded-lg font-medium">
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleCreateRoleInline}
                                                            disabled={!inlineRoleName.trim() || loading}
                                                            className="px-4 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                        >
                                                            {loading ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                            ) : (
                                                                <Check size={14} />
                                                            )}
                                                            Create Role
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 3: Organizational Structure ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Building2}
                                    title="Organizational Structure"
                                    subtitle="Team and manager assignment"
                                    sectionKey="orgStructure"
                                    stepNumber={3}
                                />
                                {expandedSections.orgStructure && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Team Selection */}
                                        {availableTeams.length > 0 && (
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2">
                                                    <Users size={16} className="inline mr-2" />
                                                    Assign to Teams
                                                </label>
                                                <div className="flex flex-wrap gap-2">
                                                    {availableTeams.map((team) => (
                                                        <button
                                                            key={team.id}
                                                            type="button"
                                                            onClick={() => handleTeamToggle(team.id)}
                                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.team_ids.includes(team.id)
                                                                ? 'bg-primary text-white'
                                                                : 'bg-muted text-foreground hover:bg-muted/80'
                                                                }`}
                                                        >
                                                            {team.team_name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Manager Selection */}
                                        <UserSearchDropdown
                                            label="Manager / Supervisor"
                                            value={formData.manager}
                                            onChange={(user) => setFormData(prev => ({ ...prev, manager: user }))}
                                            placeholder="Search for a manager..."
                                            excludeIds={getExcludeIds('manager')}
                                            helpText="The manager who will oversee this employee"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 4: Onboarding Support Team ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Heart}
                                    title="Onboarding Support Team"
                                    subtitle="Team lead, buddy, and HR contact"
                                    sectionKey="supportTeam"
                                    stepNumber={4}
                                />
                                {expandedSections.supportTeam && (
                                    <div className="px-4 pb-4 space-y-4">
                                        <UserSearchDropdown
                                            label="Team Lead"
                                            value={formData.team_lead}
                                            onChange={(user) => setFormData(prev => ({ ...prev, team_lead: user }))}
                                            placeholder="Search for team lead..."
                                            excludeIds={getExcludeIds('team_lead')}
                                            helpText="The team lead who will guide this employee"
                                        />
                                        <UserSearchDropdown
                                            label="Buddy"
                                            value={formData.buddy}
                                            onChange={(user) => setFormData(prev => ({ ...prev, buddy: user }))}
                                            placeholder="Search for buddy..."
                                            excludeIds={getExcludeIds('buddy')}
                                            helpText="A peer who will help the employee settle in"
                                        />
                                        <UserSearchDropdown
                                            label="HR Contact"
                                            value={formData.hr_contact}
                                            onChange={(user) => setFormData(prev => ({ ...prev, hr_contact: user }))}
                                            placeholder="Search for HR contact..."
                                            excludeIds={getExcludeIds('hr_contact')}
                                            helpText="Primary HR person for this employee"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 5: Status & Dates ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={Calendar}
                                    title="Status & Dates"
                                    subtitle="Lifecycle stage and important dates"
                                    sectionKey="statusDates"
                                    stepNumber={5}
                                />
                                {expandedSections.statusDates && (
                                    <div className="px-4 pb-4 space-y-4">
                                        {/* Status */}
                                        <div>
                                            <label className="block text-sm font-semibold text-foreground mb-2">Status</label>
                                            <div className="relative">
                                                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                                    {getStatusIcon(formData.lifecycle_stage)}
                                                </div>
                                                <select
                                                    name="lifecycle_stage"
                                                    value={formData.lifecycle_stage}
                                                    onChange={handleChange}
                                                    className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none bg-background text-foreground"
                                                >
                                                    <option value="pending">Pending - Account not activated</option>
                                                    <option value="preboarding">Preboarding - Before start date</option>
                                                    <option value="onboarding">Onboarding - In onboarding phase</option>
                                                    <option value="active">Active - Fully onboarded</option>
                                                    <option value="offboarding">Offboarding - In offboarding process</option>
                                                    <option value="departed">Departed - Account locked</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Dates Grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-foreground mb-2">Start Date</label>
                                                <div className="relative">
                                                    <Calendar size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                    <input
                                                        type="date"
                                                        name="start_date"
                                                        value={formData.start_date}
                                                        onChange={handleChange}
                                                        className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-background text-foreground"
                                                    />
                                                </div>
                                            </div>
                                            {formData.lifecycle_stage === 'preboarding' && (
                                                <div>
                                                    <label className="block text-sm font-semibold text-foreground mb-2">Preboarding Start Date</label>
                                                    <div className="relative">
                                                        <Clock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                                                        <input
                                                            type="date"
                                                            name="preboarding_start_date"
                                                            value={formData.preboarding_start_date}
                                                            onChange={handleChange}
                                                            className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-background text-foreground"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ─── SECTION 6: Additional Details ─── */}
                            <div className="border border-border rounded-xl overflow-visible">
                                <SectionHeader
                                    icon={FileText}
                                    title="Additional Details"
                                    subtitle="Notes and extra information"
                                    sectionKey="additionalDetails"
                                    stepNumber={6}
                                />
                                {expandedSections.additionalDetails && (
                                    <div className="px-4 pb-4">
                                        <label className="block text-sm font-semibold text-foreground mb-2">Notes</label>
                                        <div className="relative">
                                            <FileText size={20} className="absolute left-3 top-3 text-muted-foreground/70" />
                                            <textarea
                                                name="additional_details"
                                                value={formData.additional_details}
                                                onChange={handleChange}
                                                rows={3}
                                                className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none bg-background text-foreground placeholder:text-muted-foreground"
                                                placeholder="Any additional notes..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="flex-shrink-0 flex items-center gap-3 p-4 md:p-6 border-t border-border bg-muted/30">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 md:flex-none px-6 py-3 text-foreground hover:bg-muted rounded-xl font-semibold transition-colors border border-border md:border-transparent bg-card md:bg-transparent"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || showInlineRoleCreation}
                            className="flex-1 md:flex-auto px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={20} />
                                    {showEmailConfirm ? 'Confirm & Save' : 'Save Changes'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Full Create Role Modal */}
            {showCreateRoleModal && (
                <CreateRoleModal
                    isOpen={showCreateRoleModal}
                    onClose={() => {
                        setShowCreateRoleModal(false);
                        setInlineRoleData(null);
                    }}
                    onSuccess={handleFullRoleCreated}
                    initialData={inlineRoleData}
                    isEditing={false}
                />
            )}
        </>
    );
};

export default EditEmployeeModal;
